import { RouteProp } from '@react-navigation/native';
import React from 'react';
import { Platform } from 'react-native';
import { ProfilesTabParamList, useProfilesTabNavigation } from '../../../../../utils/appNavigationNames';
import { getIntl } from '../../../../../utils/intl';
import useSetHeaderTitle from '../../../../../hooks/useSetHeaderTitle';
import { ActionButtonLayout } from '../../../../v2BaseComponents/ActionButtonLayout';
import ProfileHeaderContainer from '../../../../v2BaseComponents/ProfileHeaderContainer';
import { ContentStack } from '../../../../v2BaseComponents/Stack';
import Text from '../../../../v2BaseComponents/Text';
import Alert from '../../../../v2BaseComponents/Alert';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import useFastenSandboxMode from '../../../../../hooks/useFastenSandboxMode';
import useFastenTefcaSession from '../../../../../hooks/api/useFastenTefcaSession';
import usePendingFastenConnections from '../../../../../hooks/api/usePendingFastenConnections';
import useRecordEvent from '../../../../../hooks/useRecordEvent';
import TefcaConnect, { TefcaConnectionData, TefcaWidgetEvent } from '../../../../shared/TefcaConnect';

const intl = getIntl('profile-add-tefca-connection', {
    connectViaTefca: 'Connect via TEFCA',
    intro: 'Verify your identity with CLEAR or ID.me and your records will be located across the TEFCA network.',
    unavailableTitle: 'TEFCA is not available here',
    unavailableMessage:
        'TEFCA connections are a development trial. Turn on Fasten sandbox mode in Account Information and use the web app.',
    sessionFailedTitle: 'Unable to start TEFCA',
    sessionFailedMessage: 'The TEFCA session could not be started. Check that Fasten sandbox mode is fully set up.',
    configErrorTitle: 'TEFCA is not enabled for this Fasten key',
    configErrorMessage:
        'Fasten reported a configuration error, which usually means TEFCA mode is not enabled on the sandbox organization.',
    tefcaNetwork: 'TEFCA network',
    reopenWidget: 'Open the TEFCA connector',
});

export type AddTefcaConnectionScreenOwnProps = {};

export type AddTefcaConnectionScreenProps = AddTefcaConnectionScreenOwnProps & {
    route: RouteProp<ProfilesTabParamList, 'add-tefca-connection'>;
};

const toConnections = (data: TefcaWidgetEvent['data']): TefcaConnectionData[] =>
    Array.isArray(data) ? data : data ? [data] : [];

/**
 * Hosts Fasten's widget in TEFCA mode. Web only and sandbox only: the widget is never rendered unless the staff
 * sandbox toggle is on, and the backend refuses to mint a session for anyone else.
 */
const AddTefcaConnectionScreen: React.FC<AddTefcaConnectionScreenProps> = (props) => {
    const medicalProfileId = props.route.params.profileId;
    const navigation = useProfilesTabNavigation();
    const recordEvent = useRecordEvent();
    const { isSandboxMode, isLoading: isLoadingSandboxMode } = useFastenSandboxMode();
    const isAvailable = Platform.OS === 'web' && isSandboxMode;
    const [hasConfigError, setHasConfigError] = React.useState(false);
    const hasStartedRef = React.useRef(false);

    useSetHeaderTitle(intl('connectViaTefca'));

    const { startSession, session, isStartingSession, hasSessionFailed } = useFastenTefcaSession({
        medicalProfileId,
    });
    const { addPendingConnections } = usePendingFastenConnections({ medicalProfileId, enabled: false });

    React.useEffect(() => {
        if (!isAvailable || isLoadingSandboxMode || hasStartedRef.current) return;
        hasStartedRef.current = true;
        startSession();
    }, [isAvailable, isLoadingSandboxMode, startSession]);

    // A TEFCA connection has no brand behind it, so the pending card carries the network name instead. What
    // the widget actually sends is one of the things this trial exists to learn, so the shape is recorded.
    const registerPending = React.useCallback(
        (connections: TefcaConnectionData[]) => {
            const withIds = connections.filter((connection) => !!connection.org_connection_id);
            const dropped = connections.length - withIds.length;
            if (dropped > 0) {
                recordEvent('tefcaConnectionWithoutId', { profileId: medicalProfileId, dropped: String(dropped) });
            }
            void addPendingConnections(
                withIds.map((connection) => ({
                    org_connection_id: connection.org_connection_id as string,
                    platform_type: connection.platform_type || 'tefca',
                    brand_id: connection.brand_id || '',
                    brand_name: intl('tefcaNetwork'),
                    connection_status: 'pending',
                    medicalProfileId,
                })),
            );
        },
        [addPendingConnections, medicalProfileId, recordEvent],
    );

    const handleEvent = React.useCallback(
        (event: TefcaWidgetEvent) => {
            recordEvent('tefcaWidgetEvent', {
                profileId: medicalProfileId,
                eventType: event.event_type,
                fields: toConnections(event.data)
                    .flatMap((connection) => Object.keys(connection))
                    .join(','),
            });
            switch (event.event_type) {
                case 'widget.config_error':
                    setHasConfigError(true);
                    return;
                case 'patient.connection_success':
                    registerPending(toConnections(event.data));
                    return;
                case 'widget.complete':
                    // One session can connect several organizations; each arrives as its own connection.
                    registerPending(toConnections(event.data));
                    navigation.navigate('connect', { profileId: medicalProfileId, forceFastenRefresh: 'true' });
                    return;
                default:
                    return;
            }
        },
        [registerPending, recordEvent, navigation, medicalProfileId],
    );

    return (
        <ProfileHeaderContainer profileId={medicalProfileId} omitChatButton>
            <ActionButtonLayout>
                <ContentStack>
                    <AnimateLoadedContent isLoading={isLoadingSandboxMode || isStartingSession}>
                        {() => (
                            <>
                                {!isAvailable ? (
                                    <Alert
                                        type="info"
                                        title={intl('unavailableTitle')}
                                        message={intl('unavailableMessage')}
                                    />
                                ) : null}
                                {isAvailable && hasSessionFailed ? (
                                    <Alert
                                        type="error"
                                        title={intl('sessionFailedTitle')}
                                        message={intl('sessionFailedMessage')}
                                    />
                                ) : null}
                                {isAvailable && hasConfigError ? (
                                    <Alert
                                        type="error"
                                        title={intl('configErrorTitle')}
                                        message={intl('configErrorMessage')}
                                    />
                                ) : null}
                                {isAvailable && session && !hasConfigError ? (
                                    <>
                                        <Text variant="body-1">{intl('intro')}</Text>
                                        <TefcaConnect
                                            publicId={session.publicId}
                                            externalId={session.externalId}
                                            onEvent={handleEvent}
                                            reopenLabel={intl('reopenWidget')}
                                        />
                                    </>
                                ) : null}
                            </>
                        )}
                    </AnimateLoadedContent>
                </ContentStack>
            </ActionButtonLayout>
        </ProfileHeaderContainer>
    );
};

export default React.memo(AddTefcaConnectionScreen);
